import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { partition, uniq } from "lodash";
import { ERRORS_MESSAGE, INSURANCE_QUOTE_ASSETS } from "@/constants";
import {
  createInsuranceQuote,
  userDetailsSelector,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { ApplicationType, UserDetailsState } from "@/store/slices/types/applicationFormSlice";
import { SnackbarSlice } from "@/store/slices/types/snackbar";
import { useFeatureFlags } from "@/utils/featureFlags";
import { IDispatchResponse } from "@/utils/interface";
import { getInsuranceQuoteMissingFields } from "./insuranceQuoteRequiredFields";

interface UseAutoInsuranceQuoteProps {
  applicationId: string;
  applicationType: ApplicationType;
  /** Hides the asset sections' advanced actions, the insurance quote among them. */
  hideAdvancedActions: boolean;
}

const RETRY_HINT = "Use Get Insurance Quote on the asset to try again.";

/**
 * One toast for the whole run. The snackbar shows a single message, so a toast per asset would leave only the last on
 * screen, hiding any failure before it.
 */
const getAutoQuoteSnackbar = (
  results: IDispatchResponse[],
): Pick<SnackbarSlice, "message" | "description" | "intent"> => {
  const failed = results.filter((result) => result?.error);
  const quotedCount = results.length - failed.length;

  if (failed.length === 0) {
    return {
      message:
        quotedCount === 1
          ? "Insurance quote created automatically."
          : `Insurance quotes created automatically for ${quotedCount} assets.`,
      intent: "success",
    };
  }

  const description = [
    ...uniq(failed.map((result) => result?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg)),
    RETRY_HINT,
  ];

  if (quotedCount > 0) {
    return {
      message: `Insurance quotes created automatically for ${quotedCount} of ${results.length} assets.`,
      description,
      intent: "warning",
    };
  }

  return {
    message:
      failed.length === 1
        ? "Couldn't get an insurance quote automatically."
        : `Couldn't get insurance quotes automatically for ${failed.length} assets.`,
    description,
    intent: "danger",
  };
};

/**
 * Requests insurance quotes without a click on "Get Insurance Quote" when the broker saves the last field a quote
 * needs: every asset that can be quoted at that moment is. It happens once while the application is open, and never
 * once one of its assets has a quote, so an asset added or completed afterwards is quoted by hand.
 *
 * Only a field saved while the application is open starts it, so opening an application that is already complete
 * sends nothing to the insurer, and a failed quote is not retried on every visit.
 */
export const useAutoInsuranceQuote = ({
  applicationId,
  applicationType,
  hideAdvancedActions,
}: UseAutoInsuranceQuoteProps) => {
  const dispatch = useDispatch();
  const { isAssetInsurance } = useFeatureFlags();
  const { application, gettingInsuranceQuote } = useSelector(userDetailsSelector) as UserDetailsState;
  /** Assets seen missing something a quote needs since the application was opened. */
  const incompleteAssetIds = useRef(new Set<string>());
  const autoQuotedApplicationId = useRef<string>();

  // When AssetSections shows the insurance quote section.
  const isEnabled = isAssetInsurance() && applicationType === ApplicationType.Commercial && !hideAdvancedActions;

  useEffect(() => {
    if (!applicationId || autoQuotedApplicationId.current === applicationId) return;

    const customer = application.customers?.[0];
    const entityId = application.entities?.[0]?._id;
    const assets = [application.asset, ...(application.additionalAssets ?? [])].filter((asset) => Boolean(asset?._id));
    const [readyAssets, incompleteAssets] = partition(
      assets,
      (asset) =>
        INSURANCE_QUOTE_ASSETS.includes(asset.assetType ?? "") &&
        getInsuranceQuoteMissingFields({ application, asset, customer }).length === 0,
    );
    incompleteAssets.forEach((asset) => incompleteAssetIds.current.add(asset._id));

    const hasNewlyReadyAsset = readyAssets.some((asset) => incompleteAssetIds.current.has(asset._id));
    const hasQuote = assets.some((asset) => asset.insuranceQuote);
    if (!isEnabled || !hasNewlyReadyAsset || hasQuote || !customer?._id) return;

    // An asset whose quote the broker has already asked for is left to that request.
    const assetsToQuote = readyAssets.filter((asset) => !gettingInsuranceQuote[asset._id]);
    if (assetsToQuote.length === 0) return;

    autoQuotedApplicationId.current = applicationId;
    Promise.all(
      assetsToQuote.map(
        (asset): Promise<IDispatchResponse> =>
          dispatch(createInsuranceQuote({ assetId: asset._id, customerId: customer._id, entityId, applicationId })),
      ),
    ).then((results) => dispatch(openSnackbar(getAutoQuoteSnackbar(results))));
  }, [application, applicationId, dispatch, gettingInsuranceQuote, isEnabled]);
};
